import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Smartphone,
  Download,
  Share2,
  Plus,
  MoreVertical,
  Home as HomeIcon,
  Zap,
  Wifi,
  Bell,
  CheckCircle2,
  ArrowRight,
  Play,
  RotateCcw,
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

// -----------------------------
// UA detection → default tab
// -----------------------------
const detectPlatform = () => {
  if (typeof window === 'undefined') return 'android';
  const ua = window.navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  return 'android';
};

// -----------------------------
// Reusable phone frame
// -----------------------------
const PhoneFrame = ({ children }) => (
  <div className="relative mx-auto w-[280px] sm:w-[300px] aspect-[9/19] rounded-[42px] bg-neutral-950 border border-white/10 shadow-[0_30px_80px_-20px_rgba(249,115,22,0.35)] overflow-hidden">
    {/* Notch */}
    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full bg-black z-20" />
    {/* Screen */}
    <div className="absolute inset-[6px] rounded-[36px] bg-black overflow-hidden">
      {children}
    </div>
  </div>
);

// -----------------------------
// ANDROID walkthrough (auto-playing)
// -----------------------------
const AndroidWalkthrough = ({ playKey }) => {
  return (
    <PhoneFrame>
      <style>{`
        @keyframes va-fadeInBox { 0%,20% { opacity: 0; transform: translateY(6px); } 30%,90% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-4px); } }
        @keyframes va-menu { 0%,25% { opacity:0; transform: scale(.9); } 32%,55% { opacity:1; transform: scale(1);} 60%,100% { opacity: 0; transform: scale(.95);} }
        @keyframes va-tap { 0%,50% { opacity: 0; transform: translate(-50%,-50%) scale(.4);} 55% { opacity:.9; transform: translate(-50%,-50%) scale(1);} 70%,100% { opacity: 0; transform: translate(-50%,-50%) scale(1.5);} }
        @keyframes va-toast { 0%,72% { opacity: 0; transform: translateY(20px);} 78%,95% { opacity:1; transform: translateY(0);} 100% { opacity:0; transform: translateY(6px);} }
        @keyframes va-icon-drop { 0%,80% { opacity: 0; transform: translateY(-40px) scale(.4);} 88%,100% { opacity: 1; transform: translateY(0) scale(1);} }
        @keyframes va-icon-pulse { 0%,80% { box-shadow: 0 0 0 rgba(249,115,22,0); } 90% { box-shadow: 0 0 0 8px rgba(249,115,22,.35); } 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); } }
        .va-anim { animation-duration: 8s; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
      `}</style>

      {/* Chrome-like UI */}
      <div key={playKey} className="absolute inset-0 flex flex-col text-[10px] text-white">
        {/* Status bar */}
        <div className="h-6 px-4 flex items-center justify-between text-white/70">
          <span>9:41</span>
          <div className="flex items-center gap-1"><Wifi className="w-3 h-3" /><Bell className="w-3 h-3" /></div>
        </div>
        {/* Chrome address bar */}
        <div className="mx-3 mt-1 h-8 rounded-full bg-white/10 flex items-center px-3 gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-400/70" />
          <span className="text-white/80 truncate flex-1">nubesmty.com</span>
          <MoreVertical className="w-3.5 h-3.5 text-white/70" />
        </div>

        {/* Site preview */}
        <div className="flex-1 mt-2 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-orange-500/20 via-transparent to-transparent" />
          <div className="absolute inset-0 flex flex-col items-center pt-3">
            <BrandLogo variant="badge" className="w-10 h-10" />
            <p className="mt-2 text-white/70 text-[9px]">Vape shop premium 18+</p>
            <div className="mt-3 grid grid-cols-2 gap-2 w-[180px]">
              {[0,1,2,3].map((i) => (
                <div key={i} className="aspect-square rounded-lg bg-gradient-to-br from-orange-500/25 to-neutral-900 border border-white/5" />
              ))}
            </div>
          </div>

          {/* Chrome dropdown menu — appears at 25% */}
          <div
            className="va-anim absolute top-2 right-2 w-[145px] rounded-xl bg-neutral-900 border border-white/10 shadow-xl p-1.5 space-y-0.5 opacity-0"
            style={{ animationName: 'va-menu' }}
          >
            <div className="px-2 py-1.5 rounded hover:bg-white/5 text-white/60">Nueva pestaña</div>
            <div className="px-2 py-1.5 rounded hover:bg-white/5 text-white/60">Descargas</div>
            <div className="px-2 py-1.5 rounded bg-orange-500/15 text-orange-300 font-bold flex items-center gap-1.5">
              <Download className="w-3 h-3" /> Instalar app
            </div>
            <div className="px-2 py-1.5 rounded text-white/60">Configuración</div>
          </div>

          {/* Tap ripple over "Instalar app" — appears at 55% */}
          <div
            className="va-anim absolute rounded-full bg-orange-500/50 opacity-0"
            style={{ top: '78px', left: 'calc(100% - 52px)', width: '32px', height: '32px', animationName: 'va-tap' }}
          />

          {/* Toast "Aplicación instalada" — 72–95% */}
          <div
            className="va-anim absolute bottom-6 left-1/2 -translate-x-1/2 w-[210px] px-3 py-2 rounded-lg bg-emerald-500/95 text-emerald-950 text-[10px] font-bold flex items-center gap-1.5 opacity-0"
            style={{ animationName: 'va-toast' }}
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> ¡Aplicación instalada!
          </div>

          {/* Home-screen icon drop — 80–100% */}
          <div
            className="va-anim absolute bottom-2 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 opacity-0"
            style={{ animationName: 'va-icon-drop, va-icon-pulse', animationDelay: '0s, 0s' }}
          >
            <div className="w-9 h-9 rounded-[10px] bg-black border border-orange-500/30 flex items-center justify-center overflow-hidden">
              <BrandLogo variant="badge" className="w-6 h-6" />
            </div>
            <span className="text-[8px] text-white/80 font-bold">NubesMTY</span>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
};

// -----------------------------
// iOS walkthrough (auto-playing)
// -----------------------------
const IOSWalkthrough = ({ playKey }) => {
  return (
    <PhoneFrame>
      <style>{`
        @keyframes va-share-highlight { 0%,15% { box-shadow: 0 0 0 0 rgba(249,115,22,0); } 20%,45% { box-shadow: 0 0 0 4px rgba(249,115,22,.55); } 55%,100% { box-shadow: 0 0 0 0 rgba(249,115,22,0);} }
        @keyframes va-sheet { 0%,20% { transform: translateY(100%);} 32%,72% { transform: translateY(0);} 82%,100% { transform: translateY(100%);} }
        @keyframes va-add-tap { 0%,55% { opacity: 0; transform: translate(-50%,-50%) scale(.4);} 60% { opacity:.9; transform: translate(-50%,-50%) scale(1);} 72%,100% { opacity: 0; transform: translate(-50%,-50%) scale(1.5);} }
        @keyframes va-icon-drop2 { 0%,80% { opacity: 0; transform: translateY(-40px) scale(.4);} 88%,100% { opacity: 1; transform: translateY(0) scale(1);} }
        .va-anim2 { animation-duration: 9s; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
      `}</style>

      <div key={playKey} className="absolute inset-0 flex flex-col text-[10px] text-white">
        {/* Status bar */}
        <div className="h-6 px-4 flex items-center justify-between text-white/70">
          <span>9:41</span>
          <div className="flex items-center gap-1"><Wifi className="w-3 h-3" /><Bell className="w-3 h-3" /></div>
        </div>
        {/* Safari address bar */}
        <div className="mx-3 mt-1 h-8 rounded-full bg-white/10 flex items-center px-3 gap-2">
          <span className="text-white/60 text-[9px]">AA</span>
          <span className="text-white/80 truncate flex-1 text-center">nubesmty.com</span>
        </div>

        {/* Site preview */}
        <div className="flex-1 mt-2 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-orange-500/20 via-transparent to-transparent" />
          <div className="absolute inset-0 flex flex-col items-center pt-3">
            <BrandLogo variant="badge" className="w-10 h-10" />
            <p className="mt-2 text-white/70 text-[9px]">Vape shop premium 18+</p>
            <div className="mt-3 grid grid-cols-2 gap-2 w-[180px]">
              {[0,1,2,3].map((i) => (
                <div key={i} className="aspect-square rounded-lg bg-gradient-to-br from-orange-500/25 to-neutral-900 border border-white/5" />
              ))}
            </div>
          </div>
        </div>

        {/* Safari bottom bar with Share icon highlighted */}
        <div className="h-9 flex items-center justify-around border-t border-white/10 bg-neutral-950/95 relative z-10">
          <div className="w-6 h-6 text-white/50 flex items-center justify-center">‹</div>
          <div className="w-6 h-6 text-white/50 flex items-center justify-center">›</div>
          <div
            className="va-anim2 w-7 h-7 rounded-md bg-white/5 flex items-center justify-center text-orange-300"
            style={{ animationName: 'va-share-highlight' }}
          >
            <Share2 className="w-3.5 h-3.5" />
          </div>
          <div className="w-6 h-6 text-white/50 flex items-center justify-center">☐</div>
          <div className="w-6 h-6 text-white/50 flex items-center justify-center">≡</div>
        </div>

        {/* Share Sheet — slides up 20-72% */}
        <div
          className="va-anim2 absolute inset-x-0 bottom-0 rounded-t-2xl bg-neutral-900 border-t border-white/10 p-3 space-y-2 z-20"
          style={{ animationName: 'va-sheet', transform: 'translateY(100%)' }}
        >
          <div className="mx-auto w-8 h-1 rounded-full bg-white/25" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-black border border-white/10 flex items-center justify-center overflow-hidden">
              <BrandLogo variant="badge" className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white truncate text-[10px] font-bold">NubesMTY</p>
              <p className="text-white/50 text-[8px] truncate">nubesmty.com</p>
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between rounded bg-white/[0.04] px-2 py-1.5 text-white/60">
              <span>Copiar</span><span>◨</span>
            </div>
            <div className="flex items-center justify-between rounded bg-orange-500/15 border border-orange-500/30 px-2 py-1.5 text-orange-300 font-bold">
              <span className="flex items-center gap-1.5"><Plus className="w-3 h-3" /> Agregar a pantalla de inicio</span>
              <ArrowRight className="w-3 h-3" />
            </div>
            <div className="flex items-center justify-between rounded bg-white/[0.04] px-2 py-1.5 text-white/60">
              <span>Marcador</span><span>★</span>
            </div>
          </div>

          {/* Tap ripple over the Add option */}
          <div
            className="va-anim2 absolute rounded-full bg-orange-500/50 opacity-0"
            style={{ top: '96px', left: '50%', width: '30px', height: '30px', animationName: 'va-add-tap' }}
          />
        </div>

        {/* Home-screen icon drop appearing at end */}
        <div
          className="va-anim2 absolute inset-x-0 bottom-1 flex justify-center opacity-0 z-30"
          style={{ animationName: 'va-icon-drop2' }}
        >
          <div className="flex flex-col items-center gap-1">
            <div className="w-9 h-9 rounded-[10px] bg-black border border-orange-500/30 flex items-center justify-center overflow-hidden">
              <BrandLogo variant="badge" className="w-6 h-6" />
            </div>
            <span className="text-[8px] text-white font-bold">NubesMTY</span>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
};

// -----------------------------
// Steps blocks
// -----------------------------
const STEPS = {
  android: [
    { icon: MoreVertical, title: 'Abre el menú', text: 'Toca los tres puntos (⋮) arriba a la derecha de Chrome.' },
    { icon: Download, title: 'Instalar app', text: 'Selecciona "Instalar aplicación" o "Añadir a pantalla de inicio".' },
    { icon: CheckCircle2, title: 'Confirma', text: 'Toca "Instalar". El icono de NubesMTY aparecerá en tu pantalla de inicio.' },
  ],
  ios: [
    { icon: Share2, title: 'Botón Compartir', text: 'Toca el ícono de compartir en la barra inferior de Safari.' },
    { icon: Plus, title: 'Agregar a inicio', text: 'Desliza y selecciona "Agregar a pantalla de inicio".' },
    { icon: CheckCircle2, title: 'Añadir', text: 'Toca "Añadir" arriba a la derecha. Listo, ya tienes NubesMTY como app.' },
  ],
};

const BENEFITS = [
  { icon: Zap, title: 'Acceso instantáneo', text: 'Un solo tap desde tu pantalla de inicio, sin buscar en el navegador.' },
  { icon: Smartphone, title: 'Se ve como app nativa', text: 'Sin barras de navegador. Pantalla completa, tema oscuro, súper fluida.' },
  { icon: Bell, title: 'Notificaciones', text: 'Recibirás alertas del estado de tus pedidos directo en tu celular.' },
];

// -----------------------------
// Main page
// -----------------------------
const ComoInstalarPage = () => {
  const [tab, setTab] = useState(() => detectPlatform());
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [playKey, setPlayKey] = useState(0); // restart animations

  useEffect(() => {
    // Capture deferred install prompt if it fires
    const onBIP = (e) => { e.preventDefault(); setDeferred(e); window.__pwaDeferred = e; };
    const onInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    // Also read from global if the PWAInstall banner captured it before us
    if (window.__pwaDeferred) setDeferred(window.__pwaDeferred);
    // Check already-installed
    if (window.matchMedia?.('(display-mode: standalone)').matches) setInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null);
    window.__pwaDeferred = null;
  };

  const steps = useMemo(() => STEPS[tab], [tab]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-orange-500/12 via-transparent to-transparent" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Breadcrumb */}
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-orange-400 mb-4" data-testid="pwa-back-home">
          <span>←</span> Volver a la tienda
        </Link>

        {/* Hero */}
        <div className="flex flex-col items-start gap-3 mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-orange-300 text-[11px] font-bold uppercase tracking-widest">
            <Smartphone className="w-3.5 h-3.5" /> Guía rápida · 30 segundos
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            Instala <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-red-400">NubesMTY</span><br className="hidden sm:block" /> en tu celular
          </h1>
          <p className="text-white/70 text-base max-w-2xl">
            Agrega la tienda como una <strong className="text-white">app nativa</strong> en tu pantalla de inicio.
            Sin buscar en Google, sin barras del navegador, todo con un tap. Mira el video y sigue los 3 pasos.
          </p>

          {/* Install CTA (only when Chrome deferred is available) */}
          {installed ? (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 px-4 py-2 text-sm font-bold">
              <CheckCircle2 className="w-4 h-4" /> Ya tienes la app instalada
            </div>
          ) : deferred ? (
            <button
              onClick={triggerInstall}
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-black px-5 py-2.5 text-sm font-black uppercase tracking-wide shadow-lg shadow-orange-500/25"
              data-testid="pwa-install-now-btn"
            >
              <Download className="w-4 h-4" /> Instalar ahora
            </button>
          ) : null}
        </div>

        {/* Tabs */}
        <div className="inline-flex bg-white/[0.04] border border-white/10 rounded-full p-1 mb-6" data-testid="pwa-tabs">
          <button
            onClick={() => { setTab('android'); setPlayKey((k) => k + 1); }}
            className={`px-5 h-9 rounded-full text-sm font-bold transition-colors ${tab === 'android' ? 'bg-white text-black' : 'text-white/70 hover:text-white'}`}
            data-testid="pwa-tab-android"
          >Android · Chrome</button>
          <button
            onClick={() => { setTab('ios'); setPlayKey((k) => k + 1); }}
            className={`px-5 h-9 rounded-full text-sm font-bold transition-colors ${tab === 'ios' ? 'bg-white text-black' : 'text-white/70 hover:text-white'}`}
            data-testid="pwa-tab-ios"
          >iPhone · Safari</button>
        </div>

        {/* Video + Steps grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-start">
          {/* Video (auto-loop) */}
          <div className="flex flex-col items-center gap-3">
            {tab === 'android' ? <AndroidWalkthrough playKey={playKey} /> : <IOSWalkthrough playKey={playKey} />}
            <button
              onClick={() => setPlayKey((k) => k + 1)}
              className="text-xs text-white/50 hover:text-orange-400 inline-flex items-center gap-1.5"
              data-testid="pwa-replay"
            >
              <RotateCcw className="w-3 h-3" /> Reproducir de nuevo
            </button>
            <p className="text-[10px] text-white/30 tracking-wider uppercase">Video demostrativo · 8s en bucle</p>
          </div>

          {/* Steps */}
          <div>
            <p className="text-[11px] uppercase tracking-widest text-orange-400 font-black mb-3">Pasos rápidos</p>
            <ol className="space-y-3">
              {steps.map((s, i) => {
                const Ic = s.icon;
                return (
                  <li
                    key={i}
                    className="group flex gap-4 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-orange-500/30 hover:bg-white/[0.04] p-4 sm:p-5 transition-colors"
                    data-testid={`pwa-step-${tab}-${i + 1}`}
                  >
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-black shadow-lg shadow-orange-500/25">
                      <Ic className="w-5 h-5" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="text-orange-400 text-[10px] font-black tracking-widest">PASO {i + 1}</span>
                      </div>
                      <p className="text-white font-bold text-base leading-tight">{s.title}</p>
                      <p className="text-white/60 text-sm mt-1 leading-snug">{s.text}</p>
                    </div>
                  </li>
                );
              })}
            </ol>

            {/* Extra tip per platform */}
            {tab === 'ios' && (
              <p className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-white/60 text-xs leading-snug">
                <strong className="text-white">Tip iOS:</strong> Debes abrir el sitio en <strong className="text-white">Safari</strong>. Chrome en iPhone no permite instalar apps.
              </p>
            )}
            {tab === 'android' && (
              <p className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-white/60 text-xs leading-snug">
                <strong className="text-white">Tip Android:</strong> Si no ves "Instalar aplicación" en el menú de Chrome, actualiza la página y vuelve a intentar.
              </p>
            )}
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-14">
          <p className="text-[11px] uppercase tracking-widest text-orange-400 font-black mb-3">¿Para qué instalarla?</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {BENEFITS.map((b, i) => {
              const Ic = b.icon;
              return (
                <div key={i} className="rounded-2xl border border-white/10 bg-neutral-950 p-5">
                  <div className="w-10 h-10 rounded-xl bg-orange-500/15 border border-orange-500/25 flex items-center justify-center text-orange-300 mb-3">
                    <Ic className="w-5 h-5" />
                  </div>
                  <p className="text-white font-bold">{b.title}</p>
                  <p className="text-white/60 text-sm mt-1 leading-snug">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-10 rounded-2xl border border-orange-500/25 bg-gradient-to-br from-orange-500/[0.08] to-transparent p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div>
            <p className="text-white font-black text-lg">¿Listo para vapear con estilo?</p>
            <p className="text-white/60 text-sm mt-1">Explora el catálogo mientras tu app se instala.</p>
          </div>
          <Link
            to="/catalogo"
            className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-black px-5 py-2.5 text-sm font-black uppercase tracking-wide shadow-lg shadow-orange-500/25"
            data-testid="pwa-go-catalog"
          >
            <Play className="w-4 h-4" /> Ir al catálogo
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ComoInstalarPage;
